import { Fragment, useState } from 'react';
import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { InboxIcon, MailIcon, MailOpenIcon, SearchXIcon } from 'lucide-react';
import { toast } from 'sonner';

import { TaskCriticalityEnum, TaskStatusEnum } from 'src/graphql/schema';
import { MarkAsReadTasksDocument } from 'src/graphql/actions/tasks/markAsReadTasks.mutation';
import { MarkAsUnreadTasksDocument } from 'src/graphql/actions/tasks/markAsUnreadTasks.mutation';
import { TasksDocument, type ITasksQueryVariables } from 'src/graphql/actions/tasks/tasks.query';
import { EmptyBlock } from '@/components/ui/empty-block';
import { withSuspense } from 'src/hoc/withSuspense';
import { ClientError, getApiError } from 'src/utils/errors';
import { cn } from 'src/lib/utils';
import { Button } from 'src/components/ui/button';

import { useTasksFilters, eventTypesByFilterValue, type ITimeRangeValue } from '../model';
import { useRefetchTasks } from '../api';

import type { RowSelectionState } from '@tanstack/react-table';

import TasksFilterBar from './TasksFilterBar';
import TasksTable from './TasksTable';

const TasksContainer = () => {
  const { filters, timeRange, setTimeRange, setEventTypes, setCriticality } = useTasksFilters();
  const refetchTasks = useRefetchTasks();
  const [unreadPage, setUnreadPage] = useState(1);
  const [readPage, setReadPage] = useState(1);
  const [unreadSelection, setUnreadSelection] = useState<RowSelectionState>({});
  const [readSelection, setReadSelection] = useState<RowSelectionState>({});

  const filterVariables = {
    ...(filters.createdAfter && { createdAfter: filters.createdAfter }),
    ...(filters.eventTypes && {
      eventTypes: eventTypesByFilterValue[(filters.eventTypes as keyof typeof eventTypesByFilterValue) ?? ''],
    }),
    ...(filters.criticality && { criticality: filters.criticality }),
  };

  const unreadVariables: ITasksQueryVariables = {
    ...filterVariables,
    status: TaskStatusEnum.Unread,
    page: unreadPage,
    perPage: 10,
  };

  const readVariables: ITasksQueryVariables = {
    ...filterVariables,
    status: TaskStatusEnum.Read,
    page: readPage,
    perPage: 20,
  };

  const {
    data: {
      tasks: { nodesCount: totalTasksCount },
    },
  } = useSuspenseQuery(TasksDocument);

  const { data: unreadData } = useSuspenseQuery(TasksDocument, {
    fetchPolicy: 'cache-and-network',
    variables: unreadVariables,
  });

  const { data: readData } = useSuspenseQuery(TasksDocument, {
    fetchPolicy: 'cache-and-network',
    variables: readVariables,
  });

  const [markAsRead] = useMutation(MarkAsReadTasksDocument);
  const [markAsUnread] = useMutation(MarkAsUnreadTasksDocument);

  const handleMarkAsRead = async (taskIds: string[]) => {
    try {
      const { data } = await markAsRead({ variables: { taskIds } });

      if (data?.markAsReadTasks?.success) {
        refetchTasks();
        toast.success('Task successfully marked as read');
        setUnreadSelection({});
      } else {
        throw new ClientError(data?.markAsReadTasks?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const handleMarkAsUnread = async (taskIds: string[]) => {
    try {
      const { data } = await markAsUnread({ variables: { taskIds } });

      if (data?.markAsUnreadTasks?.success) {
        refetchTasks();
        toast.success('Task successfully marked as unread');
        setReadSelection({});
      } else {
        throw new ClientError(data?.markAsUnreadTasks?.errors?.[0]);
      }
    } catch (error) {
      getApiError(error, toast.error);
    }
  };

  const handleTimeRangeChange = (value: ITimeRangeValue | null) => {
    setTimeRange(value);
    setUnreadPage(1);
    setReadPage(1);
  };

  const handleEventTypesChange = (value: string | null) => {
    setEventTypes(value);
    setUnreadPage(1);
    setReadPage(1);
  };

  const handleCriticalityChange = (value: TaskCriticalityEnum | null) => {
    setCriticality(value);
    setUnreadPage(1);
    setReadPage(1);
  };

  const unreadCount = unreadData.tasks.nodesCount;
  const readCount = readData.tasks.nodesCount;
  const noResults = Object.keys(filters).length > 1 && unreadCount === 0 && readCount === 0;

  const selectedUnreadIds = Object.keys(unreadSelection).filter((k) => unreadSelection[k]);
  const selectedReadIds = Object.keys(readSelection).filter((k) => readSelection[k]);

  if (!totalTasksCount) {
    return (
      <div className="relative grow">
        <EmptyBlock
          icon={InboxIcon}
          title="No tasks yet"
          description="Your tasks will appear here when actions require your attention."
          classes={{
            card: 'absolute top-0 left-0 w-full h-full [&>div]:h-full',
            title: 'text-base',
            content: 'h-full flex flex-col justify-center',
          }}
        />
      </div>
    );
  }

  return (
    <div className={cn('relative grow', noResults && 'flex')}>
      <div className={cn(noResults && 'flex w-full')}>
        <div className={cn('space-y-6', noResults && 'flex flex-col w-full')}>
          <TasksFilterBar
            timeRange={timeRange}
            eventTypes={filters.eventTypes}
            criticality={filters.criticality}
            onTimeRangeChange={handleTimeRangeChange}
            onEventTypesChange={handleEventTypesChange}
            onCriticalityChange={handleCriticalityChange}
          />

          {noResults ? (
            <EmptyBlock
              icon={SearchXIcon}
              title="No results found"
              description="There were no results for your search. Try another filter."
              classes={{
                card: 'grow [&>div]:h-full',
                title: 'text-base',
                content: 'h-full flex flex-col justify-center',
              }}
            />
          ) : (
            <Fragment>
              <section className="space-y-3 mb-10">
                {Boolean(selectedUnreadIds.length) && (
                  <Button size="sm" onClick={() => handleMarkAsRead(selectedUnreadIds)}>
                    <MailOpenIcon />
                    Mark as read
                  </Button>
                )}
                <TasksTable
                  type="unread"
                  totalCount={unreadCount}
                  data={unreadData.tasks}
                  page={unreadPage}
                  onPageChange={setUnreadPage}
                  rowSelection={unreadSelection}
                  onRowSelectionChange={setUnreadSelection}
                  onMarkAsRead={handleMarkAsRead}
                />
              </section>

              {Boolean(readCount) && (
                <section className="space-y-3">
                  {Boolean(selectedReadIds.length) && (
                    <Button size="sm" variant="secondary" onClick={() => handleMarkAsUnread(selectedReadIds)}>
                      <MailIcon />
                      Mark as unread
                    </Button>
                  )}
                  <TasksTable
                    type="read"
                    totalCount={readCount}
                    data={readData.tasks}
                    page={readPage}
                    onPageChange={setReadPage}
                    rowSelection={readSelection}
                    onRowSelectionChange={setReadSelection}
                    onMarkAsUnread={handleMarkAsUnread}
                  />
                </section>
              )}
            </Fragment>
          )}
        </div>
      </div>
    </div>
  );
};

export default withSuspense(TasksContainer);
